/**
 * Reine Helfer für Sammelaktionen: partitioniert eine Mehrfach-Auswahl in mutierbar vs.
 * gesperrt (GoBD), bestimmt die gemeinsame Top-Kategorie (Move-Leitplanke) und baut den
 * Ergebnis-Toast. Kein React/Server — unit-testbar.
 */
import type { PropertyFileResponse } from '@/shared/api';
import { categoryOf } from './akteFolderRules';

export interface LockedPartition {
  /** Nicht gesperrte Belege — Ziel jeder mutierenden/destruktiven Bulk-Op. */
  mutable: PropertyFileResponse[];
  /** Gesperrte (GoBD-)Belege — aus Move/Reclassify/Delete ausgenommen. */
  locked: PropertyFileResponse[];
}

/** Teilt die Auswahl in mutierbare und gesperrte Belege (Reihenfolge bleibt erhalten). */
export function partitionLocked(files: readonly PropertyFileResponse[]): LockedPartition {
  const mutable: PropertyFileResponse[] = [];
  const locked: PropertyFileResponse[] = [];
  for (const f of files) (f.locked ? locked : mutable).push(f);
  return { mutable, locked };
}

/**
 * Gemeinsame Top-Kategorie aller Dateien, oder null bei leerer oder gemischter Auswahl.
 * Grundlage der Bulk-Move-Aktivierung („nur innerhalb einer Kategorie").
 */
export function bulkCategory(files: readonly PropertyFileResponse[]): string | null {
  if (files.length === 0) return null;
  const first = categoryOf(files[0]!.folder);
  if (first === null) return null;
  return files.every((f) => categoryOf(f.folder) === first) ? first : null;
}

export interface BulkAvailability {
  /** Verschieben nur bei homogener Kategorie und mind. einem mutierbaren Beleg. */
  canMove: boolean;
  /** Reclassify/Löschen brauchen mind. einen nicht gesperrten Beleg. */
  canMutate: boolean;
  /** Anzahl gesperrter (GoBD-)Belege in der Auswahl. */
  lockedCount: number;
  /** Summe der Bytes der Auswahl — Anzeige in der Sammel-Toolbar. */
  totalBytes: number;
}

/** Was eine Auswahl zulässt. Rein abgeleitet — die Toolbar entscheidet nichts selbst. */
export function bulkAvailability(files: readonly PropertyFileResponse[]): BulkAvailability {
  const { mutable } = partitionLocked(files);
  return {
    canMove: bulkCategory(files) !== null && mutable.length > 0,
    canMutate: mutable.length > 0,
    lockedCount: files.length - mutable.length,
    totalBytes: files.reduce((sum, f) => sum + f.sizeBytes, 0),
  };
}

export interface BulkResult {
  ok: number;
  locked: number;
  fail: number;
}

/**
 * Ergebnis-Zeile „{ok} {verb} · {locked} gesperrt übersprungen · {fail} fehlgeschlagen".
 * Teile mit Zählwert 0 fallen weg (außer dem führenden ok, der den Erfolg trägt).
 */
export function bulkResultText(verb: string, r: BulkResult): string {
  const parts = [`${r.ok} ${verb}`];
  if (r.locked > 0) parts.push(`${r.locked} gesperrt übersprungen`);
  if (r.fail > 0) parts.push(`${r.fail} fehlgeschlagen`);
  return parts.join(' · ');
}
